/**
 * ApprovalsTableWidget.js
 *
 * Manager Device Approvals Widget with Table
 * Displays pending and rejected device approvals in a professional tabular format
 * Replaces PendingApprovalsWidget with improved UX using standard table component
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import ApprovalsTable from '../Tables/ApprovalsTable';
import Pagination from '../../../../shared/components/common/Pagination';
import './ApprovalsTableWidget.css';

const ApprovalsTableWidget = ({ token, user, onEditDevice, onViewDevice }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'in_draft', 'pending', 'rejected', 'approved'

  useEffect(() => {
    fetchApprovals();
    fetchDepartments();
  }, [token]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DEPARTMENTS.LIST,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.data && response.data.departments) {
        setDepartments(response.data.departments);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchApprovals = async (page = 1, filter = 'all') => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        API_ENDPOINTS.APPROVALS.MY_PENDING,
        {
          params: {
            page: page,
            per_page: perPage,
            status: filter
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data && response.data.approvals) {
        // Sort approvals: drafts first, then pending, then rejected, then approved
        const sortedApprovals = response.data.approvals.sort((a, b) => {
          const statusOrder = { 'in_draft': 1, 'pending': 2, 'rejected': 3, 'approved': 4 };
          return (statusOrder[a.approval_status] || 5) - (statusOrder[b.approval_status] || 5);
        });

        setApprovals(sortedApprovals);
        // Set the total counts from API response
        setDraftCount(response.data.draft_count || 0);
        setPendingCount(response.data.pending_count || 0);
        setRejectedCount(response.data.rejected_count || 0);
        setApprovedCount(response.data.approved_count || 0);
        // Set pagination info
        if (response.data.pagination) {
          setCurrentPage(response.data.pagination.page || 1);
          setTotalPages(response.data.pagination.total_pages || 1);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch pending approvals');
      console.error('Error fetching pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="approvals-table-widget widget-loading">
        <div className="mini-spinner"></div>
        <p>Loading approvals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="approvals-table-widget widget-error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={() => fetchApprovals(1)} className="btn-retry-small">
          Retry
        </button>
      </div>
    );
  }

  // draftCount, pendingCount, rejectedCount, and approvedCount are now retrieved from API and stored in state
  // These represent the TOTAL count across all pages, not just the visible 20 records
  // This fixes the issue where counts weren't updating when there are >20 total records

  // Determine if there are ANY approvals across all statuses
  const hasAnyApprovals = draftCount + pendingCount + rejectedCount + approvedCount > 0;

  // Get filter-specific empty message
  const getEmptyMessage = () => {
    switch (statusFilter) {
      case 'in_draft':
        return 'No draft devices yet. Create a new device to get started.';
      case 'pending':
        return 'All your devices have been approved or are still in draft.';
      case 'rejected':
        return 'No devices were rejected. Great work!';
      case 'approved':
        return 'No approved devices yet.';
      default:
        return 'No devices found.';
    }
  };

  const getFilteredTotalItems = () => {
    switch (statusFilter) {
      case 'in_draft':
        return draftCount;
      case 'pending':
        return pendingCount;
      case 'rejected':
        return rejectedCount;
      case 'approved':
        return approvedCount;
      default:
        return draftCount + pendingCount + rejectedCount + approvedCount;
    }
  };

  return (
    <div className="approvals-table-widget">
      <div className="widget-header">
        <h3>📋 My Device Approvals</h3>
        <button
          onClick={() => fetchApprovals(1, statusFilter)}
          className="btn-refresh-small"
          title="Refresh"
        >
          🔄
        </button>
      </div>

      {/* Status Summary - Always Visible with Filtering */}
      {hasAnyApprovals ? (
        <>
          <div className="approval-summary" role="tablist" aria-label="Approval status">
            <button
              type="button"
              className={`summary-item draft ${statusFilter === 'in_draft' ? 'active' : ''}`}
              data-count={draftCount}
              onClick={() => {
                setStatusFilter('in_draft');
                setTableExpanded(true);
                fetchApprovals(1, 'in_draft');
              }}
              title={`Draft devices: ${draftCount}`}
            >
              <span className="label">Draft</span>
              <span className="count">{draftCount}</span>
            </button>
            <button
              type="button"
              className={`summary-item pending ${statusFilter === 'pending' ? 'active' : ''}`}
              data-count={pendingCount}
              onClick={() => {
                setStatusFilter('pending');
                setTableExpanded(true);
                fetchApprovals(1, 'pending');
              }}
              title={`Pending approvals: ${pendingCount}`}
            >
              <span className="label">Pending</span>
              <span className="count">{pendingCount}</span>
            </button>
            <button
              type="button"
              className={`summary-item rejected ${statusFilter === 'rejected' ? 'active' : ''}`}
              data-count={rejectedCount}
              onClick={() => {
                setStatusFilter('rejected');
                setTableExpanded(true);
                fetchApprovals(1, 'rejected');
              }}
              title={`Rejected devices: ${rejectedCount}`}
            >
              <span className="label">Rejected</span>
              <span className="count">{rejectedCount}</span>
            </button>
            <button
              type="button"
              className={`summary-item approved ${statusFilter === 'approved' ? 'active' : ''}`}
              data-count={approvedCount}
              onClick={() => {
                setStatusFilter('approved');
                setTableExpanded(true);
                fetchApprovals(1, 'approved');
              }}
              title={`Approved devices: ${approvedCount}`}
            >
              <span className="label">Approved</span>
              <span className="count">{approvedCount}</span>
            </button>
            <button
              type="button"
              className={`summary-item total ${statusFilter === 'all' ? 'active' : ''}`}
              data-count={draftCount + pendingCount + rejectedCount + approvedCount}
              onClick={() => {
                setStatusFilter('all');
                setTableExpanded(true);
                fetchApprovals(1, 'all');
              }}
              title={`Total approvals: ${draftCount + pendingCount + rejectedCount + approvedCount}`}
            >
              <span className="label">All</span>
              <span className="count">{draftCount + pendingCount + rejectedCount + approvedCount}</span>
            </button>
          </div>

          {/* Table Header with Collapse/Expand Button */}
          <div className="table-section-header">
            <h4>Device Details</h4>
            <button
              onClick={() => setTableExpanded(!tableExpanded)}
              className="btn-table-collapse"
              title={tableExpanded ? 'Collapse table' : 'Expand table'}
            >
              {tableExpanded ? '▼' : '▶'}
            </button>
          </div>

          {/* Approvals Table - Collapsible */}
          {tableExpanded && (
            <div className="approvals-table-container">
              {approvals.length > 0 ? (
                <>
                  <ApprovalsTable
                    data={approvals}
                    onEditDevice={onEditDevice}
                    onViewDevice={onViewDevice}
                    loading={false}
                    departments={departments}
                    user={user}
                  />

                  {/* Pagination Controls */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => fetchApprovals(page, statusFilter)}
                    perPage={perPage}
                    onPerPageChange={(newPerPage) => {
                      setPerPage(newPerPage);
                      fetchApprovals(1, statusFilter); // Reset to page 1 when changing per_page, maintain filter
                    }}
                    totalItems={getFilteredTotalItems()}
                    itemLabel="approvals"
                  />
                </>
              ) : (
                // Empty state for current filter (but stats are still visible above)
                <div className="table-empty-state">
                  <div className="empty-icon">📭</div>
                  <p>{getEmptyMessage()}</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        // Complete empty state - shown only when there are NO approvals at all
        <div className="widget-empty">
          <div className="empty-icon">✅</div>
          <p>No device approvals to manage!</p>
          <small>All your devices have been created and approved, or you haven't created any yet.</small>
        </div>
      )}
    </div>
  );
};

export default ApprovalsTableWidget;
